import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaPlus,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from 'react-router-dom';
import Sidebar from "./Sidebar";
import ApiMappingFormModal from "./apimappingformmodal";
import ApiMappingViewModal from "./apimappingviewmodal";
import { useScreenPermissions } from '../components/useScreenPermissions';

const API_URL = "/api";

const ApiMappingMaster = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [methodFilter, setMethodFilter] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const navigate = useNavigate();
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Theme setup - identical to VendorMaster
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--sidebar-text": "#0f1724",
      "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#9aa7b0",
      "--search-icon": "#cbd5da",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    const styleId = "apimapping-master-theme";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .primary-btn { background-color: var(--accent-color); color: #ffffff; border-radius: 0; }
        .primary-btn:hover { background-color: var(--accent-hover); }

        .secondary-btn { background-color: #ffffff; color: var(--text-primary); border: 1px solid var(--page-border-color); border-radius: 0; }
        .secondary-btn:hover { background-color: #f8faf9; }

        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        .sort-active { color: #155e52 !important; }
        .sort-inactive { color: var(--sort-inactive) !important; }

        .page-active { background-color: var(--accent-color); color: #fff; border-radius: 0; }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        .table-header-cell { display: flex; align-items: center; justify-content: center; gap: 1px; }
        .table-header-label { font-weight: 700; color: #455a64; letter-spacing: 0.02em; font-size: 12px; }

        .table-header-arrow-box { width: 18px; display: flex; align-items: center; justify-content: center; }
        .table-header-sort { display: flex; flex-direction: column; align-items: center; gap: 0px; }
        .table-header-sort svg { width: 10px; height: 10px; margin: -4px 0; }

        .method-pill {
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          display: inline-block;
        }
        .method-get { background-color: #dbeafe; color: #1e40af; }
        .method-post { background-color: #d1fae5; color: #065f46; }
        .method-put { background-color: #fef3c7; color: #92400e; }
        .method-delete { background-color: #fee2e2; color: #991b1b; }
        .method-patch { background-color: #e0e7ff; color: #3730a3; }

        .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
        .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item { color: var(--sidebar-text) !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/mapping/api-mapping`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!res.ok) throw new Error("Failed to fetch API mappings");
      const data = await res.json();
      setMappings(data || []);
    } catch (error) {
      console.error("Error fetching mappings:", error);
      setMappings([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...mappings].sort((a, b) => {
      if (a[key] === null || a[key] === undefined) return 1;
      if (b[key] === null || b[key] === undefined) return -1;

      if (key === "CreatedAt") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }

      if (typeof a[key] === "string") {
        return direction === "asc"
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }

      return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
    });

    setMappings(sorted);
  };

  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="table-header-sort" aria-hidden>
        <FaSortUp className={`${active && sortConfig.direction === 'asc' ? 'sort-active' : 'sort-inactive'}`} />
        <FaSortDown className={`${active && sortConfig.direction === 'desc' ? 'sort-active' : 'sort-inactive'}`} />
      </span>
    );
  };

  const handleCreate = () => {
    setSelectedMapping(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleEdit = (mapping) => {
    setSelectedMapping(mapping);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleView = (mapping) => {
    setSelectedMapping(mapping);
    setShowView(true);
  };

  const handleDownloadCSV = () => {
    const headers = ["Id", "ApiName", "HttpMethod", "BaseUrl", "TokenUrl", "ClientId", "CreatedAt", "CreatedBy"];
    const rows = mappings.map((m) => [
      m.Id,
      m.ApiName,
      m.HttpMethod,
      m.BaseUrl,
      m.TokenUrl || "",
      m.ClientId || "",
      dayjs(m.CreatedAt).format("YYYY-MM-DD HH:mm"),
      m.CreatedBy || "",
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "api_mappings.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchMappings();
  };

  const handleLogout = () => {
    navigate('/login');
  };

  // Filtering
  const filteredMappings = mappings.filter((m) => {
    const matchesSearch =
      searchTerm === "" ||
      (m.Id && m.Id.toString().includes(searchTerm)) ||
      (m.ApiName && m.ApiName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (m.BaseUrl && m.BaseUrl.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (m.TokenUrl && m.TokenUrl.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (m.ClientId && m.ClientId.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesMethod = methodFilter === "All" || m.HttpMethod === methodFilter;

    return matchesSearch && matchesMethod;
  });

  // Pagination
  const totalPages = Math.ceil(filteredMappings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const mappingsForPage = filteredMappings.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

    const canCreate = hasFullAccess || hasPermission('API_MAPPING', 'CanCreate');
  const canView = hasFullAccess || hasPermission('API_MAPPING', 'CanView');
  const canEdit = hasFullAccess || hasPermission('API_MAPPING', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('API_MAPPING', 'CanDelete');

  const renderPagination = () => {
    const buttons = [];
    const maxVisible = 3;
    const start = Math.max(1, Math.min(currentPage - Math.floor(maxVisible / 2), Math.max(1, totalPages - maxVisible + 1)));
    const end = Math.min(totalPages, start + maxVisible - 1);

    buttons.push(
      <button key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
        className={`p-2 transition-all ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} onClick={() => handlePageChange(i)}
          className={`px-4 py-1 font-medium ${i === currentPage ? 'page-active' : 'page-inactive'}`}>
          {i}
        </button>
      );
    }

    buttons.push(
      <button key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  const sidebarTheme = {
    text: 'var(--sidebar-text, #0f1724)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} onLogout={handleLogout} />

      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">API Mapping Master</h1>
            <p className="mt-1 text-base text-gray-600">Manage and configure backend API endpoints</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium shadow-sm">
              <FaDownload className="h-4 w-4" />
              Export
            </button>
            {canCreate &&(
               <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 primary-btn font-medium shadow-sm">
              <FaPlus className="h-4 w-4" />
              Create Mapping
            </button>
            )}
           
          </div>
        </header>

        {/* Stats Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Mappings', value: mappings.length, colorClass: 'bg-blue-100 text-blue-600' },
            { label: 'GET Requests', value: mappings.filter(m => m.HttpMethod === 'GET').length, colorClass: 'bg-indigo-100 text-indigo-600' },
            { label: 'POST Requests', value: mappings.filter(m => m.HttpMethod === 'POST').length, colorClass: 'bg-emerald-100 text-emerald-600' },
            { label: 'Token Auth Used', value: mappings.filter(m => m.TokenUrl).length, colorClass: 'bg-purple-100 text-purple-600' },
          ].map((stat) => (
            <div key={stat.label} className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${stat.colorClass}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeWidth={2} d="M5 12h14M12 5v14" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Search & Filter */}
        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Mappings</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, name, URL, client ID..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full px-4 py-2 pl-10 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">HTTP Method</label>
              <select
                value={methodFilter}
                onChange={(e) => { setMethodFilter(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              >
                <option>All</option>
                <option>GET</option>
                <option>POST</option>
                <option>PUT</option>
                <option>PATCH</option>
                <option>DELETE</option>
              </select>
            </div>

            <button className="px-5 py-2 primary-btn font-medium shadow-sm" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">API Mapping List</h2>
            <div className="flex gap-2">{renderPagination()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : mappingsForPage.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No API mappings found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: 'var(--table-header-bg)' }} className="text-gray-600 text-xs font-semibold uppercase">
                  <tr>
                    <th className="px-3 py-3 text-left" onClick={() => handleSort('Id')}>
                      <div className="table-header-cell" style={{ gap: '2px' }}>
                        <span className="table-header-label">ID</span>
                        <span className="table-header-arrow-box">{renderSortArrows('Id')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left" onClick={() => handleSort('ApiName')}>
                      <div className="table-header-cell">
                        <span className="table-header-label">API Name</span>
                        <span className="table-header-arrow-box">{renderSortArrows('ApiName')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <div className="table-header-cell">
                        <span className="table-header-label">Method</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <div className="table-header-cell">
                        <span className="table-header-label">Base URL</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <div className="table-header-cell">
                        <span className="table-header-label">Token URL</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <div className="table-header-cell">
                        <span className="table-header-label">Client ID</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left" onClick={() => handleSort('CreatedAt')}>
                      <div className="table-header-cell">
                        <span className="table-header-label">Created At</span>
                        <span className="table-header-arrow-box">{renderSortArrows('CreatedAt')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <div className="table-header-cell">
                        <span className="table-header-label">Created By</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {mappingsForPage.map((mapping) => (
                    <tr key={mapping.Id} className="hover:bg-gray-50 transition-colors text-left">
                      <td className="px-6 py-3 font-medium text-gray-900">{mapping.Id}</td>
                      <td className="px-6 py-3 text-gray-900 font-semibold cursor-pointer" >
                        {mapping.ApiName}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`method-pill method-${mapping.HttpMethod?.toLowerCase() || 'get'}`}>
                          {mapping.HttpMethod || 'GET'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-600 truncate max-w-[280px]" title={mapping.BaseUrl}>
                        {mapping.BaseUrl || '-'}
                      </td>
                      <td className="px-6 py-3 text-gray-600 truncate max-w-[280px]" title={mapping.TokenUrl}>
                        {mapping.TokenUrl || '-'}
                      </td>
                      <td className="px-6 py-3 text-gray-600 truncate max-w-[200px]">{mapping.ClientId || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {mapping.CreatedAt ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900">{dayjs(mapping.CreatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-gray-600">{dayjs(mapping.CreatedAt).format("HH:mm:ss")}</span>
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-6 py-3 text-gray-600">{mapping.CreatedBy || '-'}</td>
                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button onClick={() => handleView(mapping)} title="View" className="icon-btn accent-icon-hover">
                            <FaEye className="h-4 w-4" />
                          </button>
                          )}
                          {canEdit && (
                            <button onClick={() => handleEdit(mapping)} title="Edit" className="icon-btn accent-icon-hover">
                            <FaEdit className="h-4 w-4" />
                          </button>
                          )}
                                        
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Modals */}
        {showForm && (
          <ApiMappingFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            initialData={selectedMapping}
            isEdit={isEdit}
            onSuccess={handleFormSuccess}
          />
        )}

        {showView && selectedMapping && (
          <ApiMappingViewModal
            open={showView}
            onClose={() => setShowView(false)}
            data={selectedMapping}
          />
        )}
      </main>
    </div>
  );
};

export default ApiMappingMaster;